import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, User, FileText, Clock, BarChart2, FolderOpen, Save, ShieldCheck, CalendarDays, DollarSign, AlertTriangle, Plus, Trash2, ToggleLeft, ToggleRight, GripVertical, Star, Users, ChevronDown, GraduationCap, Coffee, Sun, UsersRound } from 'lucide-react'
import api from '../lib/api'
import { useModules } from '../lib/modules'

const CCN_LEVELS = [
  { value: 'E1', label: 'E1 — Employé niv. 1', rate: 12.063 },
  { value: 'E2', label: 'E2 — Employé niv. 2', rate: 12.134 },
  { value: 'E3', label: 'E3 — Employé niv. 3', rate: 12.147 },
  { value: 'E4', label: 'E4 — Employé niv. 4', rate: 12.400 },
  { value: 'E5', label: 'E5 — Employé niv. 5', rate: 12.486 },
  { value: 'E6', label: 'E6 — Employé niv. 6', rate: 12.785 },
  { value: 'E7', label: 'E7 — Employé niv. 7', rate: 12.882 },
  { value: 'AM1', label: 'AM1 — Agent de maîtrise 1', rate: 16.023 },
  { value: 'AM2', label: 'AM2 — Agent de maîtrise 2', rate: 16.296 },
  { value: 'C1', label: 'C1 — Cadre 1', rate: 19.929 },
  { value: 'C2', label: 'C2 — Cadre 2', rate: 22.077 },
]

// Onglets : la "Fiche" porte le nouveau squelette 6-sections (refonte Phase 3, 2026-05-26).
// "Planning détaillé" garde temporairement l'ancien onglet Planning (heures perso, dispos
// hebdo, indispos récurrentes, anciens compteurs cycle 5 sem) en attendant absorption finale
// dans les sections de Fiche. Les anciens onglets "Identité" et "Contrat" sont supprimés —
// leur contenu est dans Fiche → sections 1 et 2.
const TABS = [
  { id: 'fiche',      label: 'Fiche',              icon: User },
  { id: 'admin',      label: 'Administratif',      icon: ShieldCheck },
  { id: 'planning',   label: 'Planning détaillé',  icon: CalendarDays },
  { id: 'payroll',    label: 'Paie',               icon: DollarSign },
  { id: 'counters',   label: 'Compteurs',          icon: BarChart2 },
  { id: 'history',    label: 'Historique',         icon: Clock },
  { id: 'documents',  label: 'Documents',          icon: FolderOpen },
  // Onglets legacy — accessibles pendant la transition, à supprimer une fois leurs champs
  // tous absorbés dans les 6 sections de Fiche. Voir AUDIT-FICHE-EMPLOYE-PHASE3.md.
  { id: 'identity',   label: 'Identité (legacy)',  icon: User },
  { id: 'contract',   label: 'Contrat (legacy)',   icon: FileText },
]

const JOURS_REPOS_OPTIONS = [
  { value: '',         label: '— Pas de jour fixe —' },
  { value: 'LUNDI',    label: 'Lundi' },
  { value: 'MARDI',    label: 'Mardi' },
  { value: 'MERCREDI', label: 'Mercredi' },
  { value: 'JEUDI',    label: 'Jeudi' },
  { value: 'VENDREDI', label: 'Vendredi' },
  { value: 'SAMEDI',   label: 'Samedi' },
  { value: 'DIMANCHE', label: 'Dimanche' },
]

const TYPE_HORAIRE_OPTIONS = [
  { value: '',           label: '— Indifférent —' },
  { value: 'AM',         label: 'Matin' },
  { value: 'PM',         label: 'Après-midi' },
  { value: 'JOURNEE',    label: 'Coupure (journée fractionnée)' },
  { value: 'AUXILIAIRE', label: 'Auxiliaire (samedi+dimanche)' },
]

// Front validation — cohérente avec backend (lib/employeeValidation.ts).
// Renvoie un array d'erreurs human-readable. Vide = save autorisé.
function getFrontValidationErrors(form: any): string[] {
  const errs: string[] = []
  // weeklyHours > 0 (input number min=1 normalement protégé, double-check)
  if (form.weeklyHours !== undefined && form.weeklyHours !== null && Number(form.weeklyHours) <= 0) {
    errs.push(`Quota hebdo (${form.weeklyHours}) doit être > 0`)
  }
  // dimanchesMin <= dimanchesMax legacy (les 2 doivent être numériques pour comparer)
  const min = form.dimanchesMin5Semaines
  const max = form.dimanchesMax5Semaines
  if (typeof min === 'number' && typeof max === 'number' && !isNaN(min) && !isNaN(max) && min > max) {
    errs.push(`Dimanches min (${min}) doit être ≤ Dimanches max (${max})`)
  }
  // P1.F2.c — Quotas dimanche perso (miroir validation backend) :
  //   paire matin min <= max
  const mmin = form.dimancheMatinMin, mmax = form.dimancheMatinMax
  if (typeof mmin === 'number' && typeof mmax === 'number' && !isNaN(mmin) && !isNaN(mmax) && mmin > mmax) {
    errs.push(`Dimanches matin : min (${mmin}) doit être ≤ max (${mmax})`)
  }
  //   paire après-midi min <= max
  const amin = form.dimancheApremMin, amax = form.dimancheApremMax
  if (typeof amin === 'number' && typeof amax === 'number' && !isNaN(amin) && !isNaN(amax) && amin > amax) {
    errs.push(`Dimanches après-midi : min (${amin}) doit être ≤ max (${amax})`)
  }
  //   chaque champ >= 0 (input number min=0 protège normalement, double-check)
  for (const [label, val] of [
    ['Dimanches matin min', mmin],
    ['Dimanches matin max', mmax],
    ['Dimanches après-midi min', amin],
    ['Dimanches après-midi max', amax],
    ['Dimanches journée complète groupée', form.dimancheJourneeGroupee],
  ] as const) {
    if (typeof val === 'number' && !isNaN(val) && val < 0) {
      errs.push(`${label} doit être ≥ 0`)
    }
  }
  // binomeStrict requiert binomeAvecId
  if (form.binomeStrict === true && !form.binomeAvecId) {
    errs.push('Binôme strict activé sans binôme désigné — choisis un employé ou désactive le strict')
  }
  return errs
}

// Composant local de section pliable. Pas de lib externe — inline simple Tailwind.
// Garde defaultOpen=true par défaut (cf SPEC : 6 sections ouvertes par défaut).
function Section({
  title, icon: Icon, defaultOpen = true, children,
}: { title: string; icon?: any; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="bg-white rounded-xl border border-gray-200 mb-3 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="text-blue-600" />}
          <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">{title}</h2>
        </div>
        <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-2 border-t border-gray-100 space-y-3">{children}</div>}
    </div>
  )
}

const DAYS_FR = [
  { key: 'mon', label: 'Lun' }, { key: 'tue', label: 'Mar' }, { key: 'wed', label: 'Mer' },
  { key: 'thu', label: 'Jeu' }, { key: 'fri', label: 'Ven' }, { key: 'sat', label: 'Sam' },
  { key: 'sun', label: 'Dim' },
]

const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
const lbl = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'

function getMissingFields(f: any): string[] {
  const missing: string[] = []
  if (!f.email) missing.push('Email')
  if (!f.phone) missing.push('Téléphone')
  if (!f.birthDate) missing.push('Date de naissance')
  if (!f.socialSecurityNumber) missing.push('N° Sécu')
  if (!f.contractStartDate) missing.push('Date d\'entrée')
  if (!f.dpaeDate) missing.push('DPAE')
  if (!f.ribIban) missing.push('RIB / IBAN')
  return missing
}

export default function EmployeeProfile() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const [tab, setTab] = useState('fiche')
  const { modules } = useModules()
  const rh = modules.rh
  // Module RH éteint : on ne garde que Fiche, Accès appli (ex-« Identité ») et Historique
  const tabs = rh
    ? TABS
    : TABS.filter(t => ['fiche', 'identity', 'history'].includes(t.id))
        .map(t => t.id === 'identity' ? { ...t, label: 'Accès appli & planning Excel' } : t)
  const [emp, setEmp] = useState<any>(null)
  const [form, setForm] = useState<any>({})
  const [shifts, setShifts] = useState<any[]>([])
  const [absences, setAbsences] = useState<any[]>([])
  const [docs, setDocs] = useState<any[]>([])
  const [positions, setPositions] = useState<any[]>([])
  const [establishments, setEstablishments] = useState<any[]>([])
  const [allEmployees, setAllEmployees] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [newPrime, setNewPrime] = useState({ label: '', amount: '', recurring: true })
  const [todayShifts, setTodayShifts] = useState<any[]>([])
  const [appToken, setAppToken] = useState<string | null>(null)
  const [generatingToken, setGeneratingToken] = useState(false)

  const today = new Date().toISOString().slice(0, 10)

  useEffect(() => {
    Promise.all([
      api.get(`/employees/${id}`),
      api.get(`/shifts?employeeId=${id}`),
      // Modules congés / RH éteints : pas d'appel (l'API répondrait 403)
      modules.conges ? api.get(`/absences?employeeId=${id}`) : Promise.resolve({ data: [] }),
      modules.rh ? api.get(`/documents?employeeId=${id}`) : Promise.resolve({ data: [] }),
      api.get('/positions'),
      api.get('/establishments'),
      api.get('/employees'),                       // collègues pour le select binôme
    ]).then(([empR, shiftR, absR, docR, posR, estabR, allEmpR]) => {
      const e = empR.data
      setEmp(e)
      setAppToken(e.appToken || null)
      setForm({
        ...e,
        availabilities: e.availabilities ? JSON.parse(e.availabilities) : {},
        unavailabilities: e.unavailabilities ? JSON.parse(e.unavailabilities) : [],
        primes: e.primes ? JSON.parse(e.primes) : [],
        skills: e.skills ? JSON.parse(e.skills) : [],
        joursReposPreference: e.joursReposPreference ? JSON.parse(e.joursReposPreference) : [],
        reposParSemaine: e.reposParSemaine ?? 2,
        dimancheMatinParCycle: e.dimancheMatinParCycle ?? 1,
        dimancheApremParCycle: e.dimancheApremParCycle ?? 1,
        samediParCycle: e.samediParCycle ?? 2,
        // P1 — table normalisée renvoyée par GET (incluse via Prisma)
        employeeSkills: e.employeeSkills || [],
      })
      setShifts(shiftR.data)
      setAbsences(absR.data)
      setDocs(docR.data)
      setPositions(posR.data)
      setEstablishments(estabR.data)
      setAllEmployees(allEmpR.data)

      // Charger tous les shifts du jour pour l'établissement de l'employé
      if (e.establishmentId) {
        api.get(`/shifts?establishmentId=${e.establishmentId}&from=${today}&to=${today}`)
          .then(r => setTodayShifts(r.data))
      }
    }).finally(() => setLoading(false))
  }, [id])

  async function save() {
    // Front validation — bloque AVANT l'appel réseau (cohérent avec backend P1)
    const frontErrors = getFrontValidationErrors(form)
    if (frontErrors.length > 0) {
      alert('Validation refusée (front) :\n' + frontErrors.map(e => `• ${e}`).join('\n'))
      return
    }
    setSaving(true)
    try {
      const payload: any = { ...form }
      // P1 — section Compétences : envoie le format structuré pour préserver niveau + notes.
      // Le backend accepte les 2 formats ; on privilégie le structuré quand on a employeeSkills.
      if (Array.isArray(form.employeeSkills)) {
        payload.skills = form.employeeSkills.map((s: any) => ({
          skill: s.skill, niveau: s.niveau, notes: s.notes,
        }))
      }
      delete payload.employeeSkills // pas un champ accepté par le PUT (renvoyé par GET via include)
      const res = await api.put(`/employees/${id}`, payload)
      setEmp(res.data)
      // Resync local : le backend renvoie maintenant employeeSkills à jour (P1 GET include)
      setForm((ff: any) => ({ ...ff, employeeSkills: res.data.employeeSkills || [] }))
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err: any) {
      const errors = err?.response?.data?.errors
      if (Array.isArray(errors)) {
        alert('Validation refusée :\n' + errors.map((e: any) => `• ${e.field} : ${e.message}`).join('\n'))
      } else {
        alert('Erreur de sauvegarde : ' + (err?.response?.data?.error || err.message))
      }
    } finally {
      setSaving(false)
    }
  }

  function f(label: string, key: string, type = 'text', placeholder = '') {
    return (
      <div>
        <label className={lbl}>{label}</label>
        <input type={type} value={form[key] || ''} placeholder={placeholder}
          onChange={e => setForm((ff: any) => ({ ...ff, [key]: e.target.value }))}
          className={inp} />
      </div>
    )
  }

  function toggleAvailability(day: string) {
    setForm((ff: any) => {
      const avail = { ...ff.availabilities }
      if (avail[day]) {
        delete avail[day]
      } else {
        avail[day] = [{ start: '09:00', end: '17:00' }]
      }
      return { ...ff, availabilities: avail }
    })
  }

  function addUnavailability() {
    setForm((ff: any) => ({
      ...ff,
      unavailabilities: [...(ff.unavailabilities || []), { day: 'mon', start: '00:00', end: '23:59', label: '' }]
    }))
  }

  function removeUnavailability(i: number) {
    setForm((ff: any) => ({ ...ff, unavailabilities: ff.unavailabilities.filter((_: any, idx: number) => idx !== i) }))
  }

  function addPrime() {
    if (!newPrime.label || !newPrime.amount) return
    setForm((ff: any) => ({
      ...ff,
      primes: [...(ff.primes || []), { label: newPrime.label, amount: parseFloat(newPrime.amount), recurring: newPrime.recurring }]
    }))
    setNewPrime({ label: '', amount: '', recurring: true })
  }

  function removePrime(i: number) {
    setForm((ff: any) => ({ ...ff, primes: ff.primes.filter((_: any, idx: number) => idx !== i) }))
  }

  if (loading) return <div className="p-6 text-center text-gray-400">Chargement...</div>
  if (!emp) return <div className="p-6 text-center text-gray-400">Employé introuvable</div>

  // Compteurs
  const now = new Date()
  const monthsWorked = emp.contractStartDate
    ? Math.max(1, Math.round((now.getTime() - new Date(emp.contractStartDate).getTime()) / (1000 * 60 * 60 * 24 * 30)))
    : 12
  const cpAutoAcquired = Math.min(monthsWorked * 2.5, 25)
  const cpAcquired = form.cpManual != null ? form.cpManual : cpAutoAcquired
  const cpTaken = absences.filter((a: any) => a.absenceType?.code === 'CP' && a.status === 'approved')
    .reduce((acc: number, a: any) => acc + (a.durationDays || 0), 0)
  const cpBalance = Math.max(0, cpAcquired - cpTaken)
  const totalShiftHours = shifts.reduce((acc: number, s: any) => {
    const [sh, sm] = s.startTime.split(':').map(Number)
    const [eh, em] = s.endTime.split(':').map(Number)
    return acc + Math.max(0, ((eh * 60 + em) - (sh * 60 + sm) - (s.breakMinutes || 0)) / 60)
  }, 0)
  const MONTHS = ['jan','fév','mar','avr','mai','juin','juil','août','sep','oct','nov','déc']

  const missingFields = rh ? getMissingFields(form) : []

  // Calcul coût employeur
  const monthlySalary = (form.weeklyHours || 35) * 4.33 * (form.hourlyRate || 12.063)
  const employerCost = monthlySalary * (1 + (form.employerCostRate || 45) / 100)
  const hourlyEmployerCost = employerCost / ((form.weeklyHours || 35) * 4.33)

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <Link to="/employees" className="text-gray-400 hover:text-gray-600"><ArrowLeft size={20} /></Link>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
            {emp.firstName[0]}{emp.lastName !== '-' ? emp.lastName[0] : ''}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{emp.firstName} {emp.lastName !== '-' ? emp.lastName : ''}</h1>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {emp.matricule && <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{emp.matricule}</span>}
              <span className="text-sm text-gray-500">{emp.contractType} · {emp.weeklyHours}h/sem</span>
              <span className="text-xs bg-blue-100 text-blue-700 font-medium px-2 py-0.5 rounded-full">{emp.conventionLevel}</span>
              {emp.position && <span className="text-xs px-2 py-0.5 rounded-full text-white font-medium" style={{ backgroundColor: emp.position.color }}>{emp.position.name}</span>}
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${emp.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {emp.status === 'active' ? 'Actif' : 'Archivé'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            if (!confirm('Dupliquer cet employé ? Les données personnelles (email, tél, IBAN…) ne seront pas copiées.')) return
            try {
              const r = await api.post(`/employees/${id}/duplicate`)
              nav(`/employees/${r.data.id}`)
            } catch { alert('Erreur lors de la duplication') }
          }} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700">
            <Users size={15} /> Dupliquer
          </button>
          {emp.status === 'active' ? (
            <button onClick={async () => {
              if (!confirm(`Archiver ${emp.firstName} ${emp.lastName} ? L'employé ne sera plus actif mais ses données seront conservées.`)) return
              try { await api.post(`/employees/${id}/archive`); nav('/employees') } catch { alert('Erreur') }
            }} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-700">
              <ToggleLeft size={15} /> Archiver
            </button>
          ) : (
            <button onClick={async () => {
              try { const r = await api.post(`/employees/${id}/restore`); setEmp(r.data) } catch { alert('Erreur') }
            }} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-green-300 bg-green-50 hover:bg-green-100 text-green-700">
              <ToggleRight size={15} /> Réactiver
            </button>
          )}
          <button onClick={save} disabled={saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${saved ? 'bg-green-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'} disabled:opacity-50`}>
            <Save size={15} />{saved ? 'Enregistré ✓' : saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Alerte infos manquantes */}
      {missingFields.length > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
          <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Informations manquantes</p>
            <p className="text-xs text-amber-600 mt-0.5">{missingFields.join(' · ')}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 mb-6 border-b border-gray-200 overflow-x-auto">
        {tabs.map(t => {
          const Icon = t.icon
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
                tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <Icon size={14} />{t.label}
            </button>
          )
        })}
      </div>

      {/* ── FICHE (refonte Phase 3 — 6 sections pliables, ouvertes par défaut) ── */}
      {tab === 'fiche' && (
        <div>
          {/* SECTION 1 — Identité */}
          <Section title="1. Identité" icon={User}>
            <div className="grid grid-cols-2 gap-3">
              {f('Prénom *', 'firstName')}
              {f('Nom *', 'lastName')}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Magasin</label>
                <select value={form.establishmentId || ''}
                  onChange={e => setForm((ff: any) => ({ ...ff, establishmentId: e.target.value }))}
                  className={inp}>
                  {establishments.map((e: any) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Statut RH (dérivé de Contrat)</label>
                <input value={form.contractType || ''} readOnly
                  className={`${inp} bg-gray-50 text-gray-500`} />
                <p className="text-xs text-gray-400 mt-1">À éditer dans la section Contrat ci-dessous.</p>
              </div>
            </div>
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <div>
                <p className="text-sm font-medium text-amber-900">Compté dans l'effectif terrain</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  OFF = dirigeant hors quota (Manu, Gilbert). Remplace l'ancienne règle <code>if (fn === 'gilbert')</code> en dur.
                </p>
              </div>
              <button type="button"
                onClick={() => setForm((ff: any) => ({ ...ff, compteDansEffectif: !ff.compteDansEffectif }))}
                className={form.compteDansEffectif ? 'text-blue-600' : 'text-gray-400'}>
                {form.compteDansEffectif ? <ToggleRight size={30} /> : <ToggleLeft size={30} />}
              </button>
            </div>
          </Section>

          {/* QSE — arrivée d'un salarié : preuves exigées par l'audit (livret, formations, visite médicale) */}
          <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-4">
            <div>
              <p className="text-sm font-medium text-emerald-900">Nouvel embauché : échéances QSE</p>
              <p className="text-xs text-emerald-700 mt-0.5">Livret d'accueil le jour J, extincteurs à J+7, e-learning hygiène à J+30, visite médicale à J+90 (à partir de la date de début).</p>
            </div>
            <button type="button" onClick={async () => {
              try {
                const r = await api.post('/qse/compliance/new-hire', { employeeId: id, ...(form.contractStartDate ? { date: String(form.contractStartDate).slice(0, 10) } : {}) })
                alert(r.data.created ? `✅ ${r.data.created} échéance(s) créée(s) dans Conformité QSE` : 'Déjà créées pour cette personne')
              } catch (e: any) { alert(e?.response?.data?.error || 'Création impossible') }
            }} className="flex-shrink-0 bg-emerald-600 text-white text-sm font-medium px-3 py-2 rounded-lg">Créer les échéances</button>
          </div>

          {/* SECTION 2 — Contrat & quota terrain */}
          <Section title="2. Contrat & quota terrain" icon={FileText}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Type de contrat</label>
                <select value={form.contractType || 'CDI'}
                  onChange={e => setForm((ff: any) => ({ ...ff, contractType: e.target.value }))}
                  className={inp}>
                  {['CDI','CDD','Étudiant','Saisonnier','Extra','Stage','Intérim'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Quota hebdo (heures)</label>
                <input type="number" min={1} max={60} value={form.weeklyHours || 35}
                  onChange={e => setForm((ff: any) => ({ ...ff, weeklyHours: +e.target.value }))} className={inp} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {f('Date de début', 'contractStartDate', 'date')}
              {(form.contractType === 'CDD' || form.contractType === 'Étudiant' || form.contractType === 'Stage')
                ? f('Date de fin', 'contractEndDate', 'date')
                : <div />}
            </div>
            <div>
              <label className={lbl}>Notes contrat (optionnel)</label>
              <textarea value={form.notesContrat || ''} rows={2}
                placeholder="Particularités CDD, avenants, etc."
                onChange={e => setForm((ff: any) => ({ ...ff, notesContrat: e.target.value }))}
                className={`${inp} resize-none`} />
            </div>
          </Section>

          {/* SECTION 3 — Compétences (lit/écrit EmployeeSkill via format structuré) */}
          <Section title="3. Compétences (rayon / poste)" icon={GraduationCap}>
            <p className="text-xs text-gray-500">
              Ordonnées par priorité — la 1ère ligne est la compétence principale. Choisis le skill dans la liste
              des postes configurés, puis ajuste niveau (défaut : prio 1=PRINCIPAL, 2=SUPPLEANT, 3+=DEPANNAGE,
              modifiable) et notes manager (optionnel). L'ordre = priorité, ↑/↓ pour reclasser.
            </p>
            {positions.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                ⚠️ Aucun poste configuré pour cette organisation.{' '}
                <Link to="/positions" className="underline font-medium hover:text-amber-900">
                  Va configurer les postes
                </Link>{' '}
                avant de renseigner les compétences.
              </div>
            )}
            <div className="space-y-2">
              {(form.employeeSkills || []).length === 0 && (
                <p className="text-xs text-gray-400 italic">Aucune compétence renseignée.</p>
              )}
              {(form.employeeSkills || []).map((es: any, i: number) => {
                const total = (form.employeeSkills || []).length
                // Filtre : on n'affiche pas les skills déjà choisis par d'autres lignes (dedupe UX —
                // la contrainte @@unique([employeeId, skill]) ferait planter le save sinon).
                const usedByOthers = new Set(
                  (form.employeeSkills || [])
                    .filter((_: any, idx: number) => idx !== i)
                    .map((s: any) => s.skill)
                    .filter(Boolean)
                )
                const optionsForRow = positions.filter((p: any) => !usedByOthers.has(p.name))
                const currentIsOrphan = !!es.skill && !positions.some((p: any) => p.name === es.skill)

                const upd = (patch: any) => {
                  const arr = [...form.employeeSkills]
                  arr[i] = { ...es, ...patch }
                  setForm((ff: any) => ({ ...ff, employeeSkills: arr }))
                }
                const move = (delta: number) => {
                  const j = i + delta
                  if (j < 0 || j >= total) return
                  const arr = [...form.employeeSkills]
                  ;[arr[i], arr[j]] = [arr[j], arr[i]]
                  arr.forEach((r: any, idx: number) => { r.priorite = idx + 1 })
                  setForm((ff: any) => ({ ...ff, employeeSkills: arr }))
                }
                const remove = () => {
                  const arr = form.employeeSkills.filter((_: any, idx: number) => idx !== i)
                  arr.forEach((r: any, idx: number) => { r.priorite = idx + 1 })
                  setForm((ff: any) => ({ ...ff, employeeSkills: arr }))
                }

                return (
                  <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <GripVertical size={14} className="text-gray-300 flex-shrink-0" />
                    <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      i === 0 ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-500'
                    }`} title={`Priorité ${i + 1}`}>
                      {i === 0 ? <Star size={11} /> : i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <select value={es.skill || ''}
                        onChange={e => upd({ skill: e.target.value })}
                        className={`w-full border rounded px-2 py-1 text-sm ${currentIsOrphan ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                        title={currentIsOrphan ? `Skill « ${es.skill} » n'est plus dans les postes configurés` : undefined}>
                        <option value="">— Choisir un poste —</option>
                        {optionsForRow.map((p: any) => (
                          <option key={p.id} value={p.name}>{p.name}</option>
                        ))}
                        {currentIsOrphan && (
                          <option value={es.skill}>{es.skill} (non listé — legacy)</option>
                        )}
                      </select>
                    </div>
                    <select value={es.niveau || 'PRINCIPAL'}
                      onChange={e => upd({ niveau: e.target.value })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm flex-shrink-0"
                      title="Niveau : PRINCIPAL = expert / autonome ; SUPPLEANT = sait faire ; DEPANNAGE = en renfort">
                      <option value="PRINCIPAL">PRINCIPAL</option>
                      <option value="SUPPLEANT">SUPPLEANT</option>
                      <option value="DEPANNAGE">DEPANNAGE</option>
                    </select>
                    <input value={es.notes || ''} placeholder="Notes (ex: rapide, à former, ...)"
                      onChange={e => upd({ notes: e.target.value || null })}
                      className="w-44 border border-gray-300 rounded px-2 py-1 text-sm flex-shrink-0" />
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button type="button" onClick={() => move(-1)} disabled={i === 0}
                        className="text-gray-400 hover:text-blue-500 disabled:opacity-25 px-1.5 py-1 rounded hover:bg-gray-50"
                        title="Monter (priorité supérieure)">↑</button>
                      <button type="button" onClick={() => move(1)} disabled={i === total - 1}
                        className="text-gray-400 hover:text-blue-500 disabled:opacity-25 px-1.5 py-1 rounded hover:bg-gray-50"
                        title="Descendre (priorité inférieure)">↓</button>
                      <button type="button" onClick={remove}
                        className="text-gray-300 hover:text-red-500 px-1.5 py-1 rounded hover:bg-red-50"
                        title="Supprimer cette compétence">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="flex items-center justify-between pt-1">
              <button type="button"
                onClick={() => setForm((ff: any) => {
                  const arr = [...(ff.employeeSkills || [])]
                  const priorite = arr.length + 1
                  const niveau = priorite === 1 ? 'PRINCIPAL' : priorite === 2 ? 'SUPPLEANT' : 'DEPANNAGE'
                  // Pré-sélectionne le 1er poste non encore utilisé (UX : évite "— Choisir —" vide)
                  const used = new Set(arr.map((s: any) => s.skill).filter(Boolean))
                  const firstAvailable = positions.find((p: any) => !used.has(p.name))?.name || ''
                  arr.push({ skill: firstAvailable, priorite, niveau, notes: null })
                  return { ...ff, employeeSkills: arr }
                })}
                disabled={positions.length === 0 || (form.employeeSkills || []).length >= positions.length}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                <Plus size={13} /> Ajouter une compétence
              </button>
              {(form.employeeSkills || []).length > 0 && positions.length > 0 && (
                <span className="text-[11px] text-gray-400">
                  {(form.employeeSkills || []).length} / {positions.length} postes
                </span>
              )}
            </div>
          </Section>

          {/* SECTION 4 — Préférences horaires */}
          <Section title="4. Préférences horaires" icon={Coffee}>
            <p className="text-xs text-gray-500">
              Préférence par défaut. Si <strong>contrainte ferme</strong> est ON, l'algo
              NE PEUT PAS violer la préférence (cas Salim = F&L matin obligé). Sinon préférence
              souple, l'algo peut s'en écarter pour combler les trous.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Préférence créneau</label>
                <select value={form.typeHoraire || ''}
                  onChange={e => setForm((ff: any) => ({
                    ...ff,
                    typeHoraire: e.target.value || null,
                    // Si on bascule en Indifférent, "contrainte ferme sur indifférent" n'a pas
                    // de sens → on désactive automatiquement.
                    creneauObligatoire: e.target.value ? ff.creneauObligatoire : false,
                  }))}
                  className={inp}>
                  {TYPE_HORAIRE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className={`flex items-center justify-between rounded-xl px-3 py-2 border ${
                form.typeHoraire ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
              }`}>
                <div>
                  <p className={`text-sm font-medium ${form.typeHoraire ? 'text-amber-900' : 'text-gray-400'}`}>
                    Contrainte ferme
                  </p>
                  <p className={`text-xs mt-0.5 ${form.typeHoraire ? 'text-amber-700' : 'text-gray-400'}`}>
                    {form.typeHoraire
                      ? `ON = l'algo NE PEUT PAS violer cette préférence`
                      : `Indisponible tant que la préférence est Indifférent`}
                  </p>
                </div>
                <button type="button"
                  disabled={!form.typeHoraire}
                  onClick={() => setForm((ff: any) => ({ ...ff, creneauObligatoire: !ff.creneauObligatoire }))}
                  className={`${
                    form.creneauObligatoire && form.typeHoraire ? 'text-blue-600' : 'text-gray-400'
                  } disabled:opacity-30 disabled:cursor-not-allowed`}>
                  {form.creneauObligatoire ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                </button>
              </div>
            </div>
            <div>
              <label className={lbl}>Notes horaires (optionnel)</label>
              <input value={form.notesHoraires || ''} placeholder="Ex: préfère matin sauf mercredi"
                onChange={e => setForm((ff: any) => ({ ...ff, notesHoraires: e.target.value || null }))}
                className={inp} />
            </div>
          </Section>

          {/* SECTION 5 — Repos & dimanches (cycle 5 semaines) */}
          <Section title="5. Repos & dimanches (cycle 5 semaines)" icon={Sun}>
            <p className="text-xs text-gray-500">
              Jour de repos hebdo habituel + quotas de dimanches travaillés sur cycle 5 semaines.
              Les quotas matin / après-midi sont séparés (l'après-midi est payé double).
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Jour de repos fixe</label>
                <select value={form.jourReposFixe || ''}
                  onChange={e => setForm((ff: any) => ({ ...ff, jourReposFixe: e.target.value || null }))}
                  className={inp}>
                  {JOURS_REPOS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className={`flex items-center justify-between rounded-xl px-3 py-2 border ${
                form.jourReposFixe ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
              }`}>
                <div>
                  <p className={`text-sm font-medium ${form.jourReposFixe ? 'text-amber-900' : 'text-gray-400'}`}>
                    Repos décalable
                  </p>
                  <p className={`text-xs mt-0.5 ${form.jourReposFixe ? 'text-amber-700' : 'text-gray-400'}`}>
                    {form.jourReposFixe
                      ? `ON = l'algo peut décaler (ex: si bosse dimanche, repos un autre jour)`
                      : `Pas de jour fixe = non applicable`}
                  </p>
                </div>
                <button type="button"
                  disabled={!form.jourReposFixe}
                  onClick={() => setForm((ff: any) => ({ ...ff, jourReposFixeFlexible: !ff.jourReposFixeFlexible }))}
                  className={`${
                    form.jourReposFixeFlexible && form.jourReposFixe ? 'text-blue-600' : 'text-gray-400'
                  } disabled:opacity-30 disabled:cursor-not-allowed`}>
                  {form.jourReposFixeFlexible ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                </button>
              </div>
            </div>

            {/* P1.F2.a/c — Quotas dimanche perso (cycle 5 semaines) */}
            {(() => {
              const matinInvalid = typeof form.dimancheMatinMin === 'number' &&
                                   typeof form.dimancheMatinMax === 'number' &&
                                   form.dimancheMatinMin > form.dimancheMatinMax
              const apremInvalid = typeof form.dimancheApremMin === 'number' &&
                                   typeof form.dimancheApremMax === 'number' &&
                                   form.dimancheApremMin > form.dimancheApremMax
              const matinClass = `${inp} ${matinInvalid ? 'border-red-400 bg-red-50' : ''}`
              const apremClass = `${inp} ${apremInvalid ? 'border-red-400 bg-red-50' : ''}`
              return (
                <div className="border border-gray-200 rounded-xl p-3 bg-white space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Quotas dimanche perso (cycle 5 semaines)</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Combien de dimanches l'employé peut/doit travailler par cycle 5 semaines.
                      Par défaut tout à 0 = jamais planifié dimanche.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Paire matin */}
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-gray-700">Dimanches matin</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-500 uppercase">Min</label>
                          <input type="number" min={0} value={form.dimancheMatinMin ?? 0}
                            onChange={e => setForm((ff: any) => ({ ...ff, dimancheMatinMin: +e.target.value }))}
                            className={matinClass} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 uppercase">Max</label>
                          <input type="number" min={0} value={form.dimancheMatinMax ?? 0}
                            onChange={e => setForm((ff: any) => ({ ...ff, dimancheMatinMax: +e.target.value }))}
                            className={matinClass} />
                        </div>
                      </div>
                      {matinInvalid && (
                        <p className="text-xs text-red-600">
                          ⚠️ Min ({form.dimancheMatinMin}) doit être ≤ Max ({form.dimancheMatinMax}).
                        </p>
                      )}
                    </div>
                    {/* Paire après-midi */}
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-gray-700">
                        Dimanches après-midi <span className="text-gray-400 font-normal">(payé double)</span>
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-500 uppercase">Min</label>
                          <input type="number" min={0} value={form.dimancheApremMin ?? 0}
                            onChange={e => setForm((ff: any) => ({ ...ff, dimancheApremMin: +e.target.value }))}
                            className={apremClass} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 uppercase">Max</label>
                          <input type="number" min={0} value={form.dimancheApremMax ?? 0}
                            onChange={e => setForm((ff: any) => ({ ...ff, dimancheApremMax: +e.target.value }))}
                            className={apremClass} />
                        </div>
                      </div>
                      {apremInvalid && (
                        <p className="text-xs text-red-600">
                          ⚠️ Min ({form.dimancheApremMin}) doit être ≤ Max ({form.dimancheApremMax}).
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Compteur standalone */}
                  <div className="border-t border-gray-100 pt-2">
                    <label className="block text-xs font-semibold text-gray-700">
                      Dimanches en journée complète groupée
                    </label>
                    <p className="text-[11px] text-gray-500 mb-1">
                      Compteur : nombre de dimanches matin + après-midi le même jour, sur cycle 5 semaines.
                    </p>
                    <input type="number" min={0} value={form.dimancheJourneeGroupee ?? 0}
                      onChange={e => setForm((ff: any) => ({ ...ff, dimancheJourneeGroupee: +e.target.value }))}
                      className={`${inp} w-32`} />
                  </div>
                </div>
              )
            })()}

            {/* Legacy : 3 champs P3 plus lus par l'algo, conservés transitoirement */}
            <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 space-y-2">
              <div>
                <p className="text-xs font-semibold text-amber-900">⚠ DÉPRÉCIÉ — sera supprimé après stabilité P1.F2.d</p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  Ces 3 champs sont remplacés par les <em>Quotas dimanche perso</em> ci-dessus.
                  Plus lus par l'algo. Conservés transitoirement pour rollback.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={lbl}>Dimanches min / cycle 5 sem</label>
                  <input type="number" min={0} value={form.dimanchesMin5Semaines ?? 0}
                    onChange={e => setForm((ff: any) => ({ ...ff, dimanchesMin5Semaines: +e.target.value }))}
                    className={`${inp} ${
                      typeof form.dimanchesMin5Semaines === 'number' &&
                      typeof form.dimanchesMax5Semaines === 'number' &&
                      form.dimanchesMin5Semaines > form.dimanchesMax5Semaines
                        ? 'border-red-400 bg-red-50'
                        : ''
                    }`} />
                </div>
                <div>
                  <label className={lbl}>Dimanches max / cycle 5 sem</label>
                  <input type="number" min={0} value={form.dimanchesMax5Semaines ?? ''}
                    placeholder="vide = pas de plafond"
                    onChange={e => setForm((ff: any) => ({
                      ...ff,
                      dimanchesMax5Semaines: e.target.value === '' ? null : +e.target.value,
                    }))}
                    className={`${inp} ${
                      typeof form.dimanchesMin5Semaines === 'number' &&
                      typeof form.dimanchesMax5Semaines === 'number' &&
                      form.dimanchesMin5Semaines > form.dimanchesMax5Semaines
                        ? 'border-red-400 bg-red-50'
                        : ''
                    }`} />
                </div>
                <div>
                  <label className={lbl}>Préférence créneau dimanche</label>
                  <select value={form.prefCreneauDimanche || 'INDIFFERENT'}
                    onChange={e => setForm((ff: any) => ({ ...ff, prefCreneauDimanche: e.target.value }))}
                    className={inp}>
                    <option value="INDIFFERENT">Indifférent</option>
                    <option value="MATIN">Matin</option>
                    <option value="APREM">Après-midi</option>
                    <optgroup label="—— Cas spécial ——">
                      <option value="JOURNEE_COMPLETE">Journée complète</option>
                    </optgroup>
                  </select>
                </div>
              </div>
              {typeof form.dimanchesMin5Semaines === 'number' &&
               typeof form.dimanchesMax5Semaines === 'number' &&
               form.dimanchesMin5Semaines > form.dimanchesMax5Semaines && (
                <p className="text-xs text-red-600">
                  ⚠️ Dimanches min ({form.dimanchesMin5Semaines}) doit être ≤ max ({form.dimanchesMax5Semaines}). La sauvegarde sera refusée.
                </p>
              )}
            </div>
            {/* P1.G — règle livraison (visible uniquement pour les dirigeants hors quota) */}
            {form.compteDansEffectif === false && (
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-2">
                <div>
                  <p className="text-sm font-medium text-amber-900">
                    Disponible uniquement les matins de jours de livraison
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Cas dirigeant faisant des appoints sur jours de livraison (ex : mardi + vendredi
                    en basse saison). L'employé sera planifié hors quota standard, en plus du min/max
                    du créneau.
                  </p>
                </div>
                <button type="button"
                  onClick={async () => {
                    // P1.G — auto-save dédié via endpoint POST /:id/toggle-livraison.
                    // Endpoint ciblé (lookup par id seul, sans org check) pour éviter les soucis
                    // d'org-mismatch dans des sessions avec token désynchronisé.
                    // Optimistic UI + rollback sur erreur.
                    const newVal = !form.shiftUniquementJoursLivraison
                    setForm((ff: any) => ({ ...ff, shiftUniquementJoursLivraison: newVal }))
                    try {
                      await api.post(`/employees/${id}/toggle-livraison`, { value: newVal })
                    } catch (err: any) {
                      setForm((ff: any) => ({ ...ff, shiftUniquementJoursLivraison: !newVal }))
                      alert('Erreur enregistrement règle livraison : ' + (err?.response?.data?.error || err.message))
                    }
                  }}
                  className={form.shiftUniquementJoursLivraison ? 'text-blue-600' : 'text-gray-400'}>
                  {form.shiftUniquementJoursLivraison ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                </button>
              </div>
            )}
          </Section>

          {/* SECTION 6 — Binômage & notes manager */}
          <Section title="6. Binômage & notes manager" icon={UsersRound}>
            <p className="text-xs text-gray-500">
              Lier cet employé à un autre du même magasin. <strong>Strict</strong> ON = l'algo les
              planifie toujours ensemble (cas Gilbert/Petchara). OFF = simple préférence souple.
              <span className="block mt-1 text-amber-700">
                ⚠️ Liaison unilatérale dans cette UI — si tu veux un binôme réciproque, édite aussi la fiche de l'autre employé.
              </span>
            </p>
            <div>
              <label className={lbl}>Binôme avec</label>
              {(() => {
                const sameStore = allEmployees.filter(
                  (e: any) => e.id !== id && e.establishmentId === form.establishmentId
                )
                if (!form.establishmentId) {
                  return (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      Pas de magasin défini sur cette fiche — impossible de proposer un binôme.
                    </p>
                  )
                }
                if (sameStore.length === 0) {
                  return (
                    <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                      Aucun autre employé actif dans ce magasin.
                    </p>
                  )
                }
                return (
                  <select value={form.binomeAvecId || ''}
                    onChange={e => setForm((ff: any) => ({
                      ...ff,
                      binomeAvecId: e.target.value || null,
                      // Si on retire le binôme, le strict perd son sens
                      binomeStrict: e.target.value ? ff.binomeStrict : false,
                    }))}
                    className={inp}>
                    <option value="">— Pas de binôme —</option>
                    {sameStore.map((e: any) => (
                      <option key={e.id} value={e.id}>
                        {e.firstName} {e.lastName !== '-' ? e.lastName : ''}
                      </option>
                    ))}
                  </select>
                )
              })()}
            </div>
            {form.binomeAvecId && (
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-amber-900">Binôme strict</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    ON = toujours ensemble (cas Gilbert/Petchara). OFF = préférence souple.
                  </p>
                </div>
                <button type="button"
                  onClick={() => setForm((ff: any) => ({ ...ff, binomeStrict: !ff.binomeStrict }))}
                  className={form.binomeStrict ? 'text-blue-600' : 'text-gray-400'}>
                  {form.binomeStrict ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                </button>
              </div>
            )}
            <div>
              <label className={lbl}>Notes manager (confidentielles)</label>
              <textarea value={form.notes || ''} rows={3}
                placeholder="Notes diverses du manager — visible uniquement par les admins."
                onChange={e => setForm((ff: any) => ({ ...ff, notes: e.target.value }))}
                className={`${inp} resize-none`} />
            </div>
          </Section>
        </div>
      )}

      {/* ── IDENTITÉ (legacy — préservé tant que les sections Fiche n'ont pas absorbé tous les champs) ── */}
      {tab === 'identity' && (
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-4">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Informations personnelles</h2>
            <div className="grid grid-cols-2 gap-3">
              {f('Prénom *', 'firstName')}
              {f('Nom *', 'lastName')}
            </div>
            <div>
              <label className={lbl}>Nom dans le planning Excel</label>
              <input value={form.nomPlanning || ''} placeholder="Ex. : tel qu'écrit en colonne D du fichier planning"
                onChange={e => setForm((ff: any) => ({ ...ff, nomPlanning: e.target.value }))}
                className={inp} />
              <p className="text-xs text-gray-400 mt-1">Sert à reconnaître l'employé quand on charge le planning Excel.</p>
            </div>
            {rh && <>
            <div>
              <label className={lbl}>Genre</label>
              <select value={form.gender || ''} onChange={e => setForm((ff: any) => ({ ...ff, gender: e.target.value }))} className={inp}>
                <option value="">Non renseigné</option>
                <option value="M">Homme</option>
                <option value="F">Femme</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            {f('Date de naissance', 'birthDate', 'date')}
            {f('Nationalité', 'nationality', 'text', 'Française')}
            </>}
            {f('Email', 'email', 'email', 'prenom.nom@email.fr')}
            {f('Téléphone', 'phone', 'tel', '06 00 00 00 00')}
            {rh && f('Adresse', 'address', 'text', '12 rue de la Paix, 13260 Cassis')}
          </div>
          <div className="space-y-4">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">{rh ? 'Données internes' : 'Accès à l’appli employé'}</h2>
            {rh && f('Matricule', 'matricule', 'text', 'EMP-001')}
            {rh && f('N° Sécurité sociale', 'socialSecurityNumber', 'text', '1 XX XX XX XXX XXX XX')}
            {rh && f('Contact urgence', 'emergencyContact', 'text', 'Marie Martin — 06 00 00 00 00')}
            <div>
              <label className={lbl}>{rh ? 'Badge PIN' : 'Code PIN (connexion à l’appli)'}</label>
              <input value={form.badgePin || ''} maxLength={4} placeholder="4 chiffres"
                onChange={e => setForm((ff: any) => ({ ...ff, badgePin: e.target.value }))}
                className={inp} />
            </div>
            <div>
              <label className={lbl}>Lien personnel app employé</label>
              {appToken ? (
                <div className="mt-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <input readOnly value={`${window.location.origin}/app/e/${appToken}`}
                      className="flex-1 border rounded-lg px-3 py-2 text-xs text-gray-600 bg-gray-50 font-mono" />
                    <button type="button"
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/app/e/${appToken}`); alert('Lien copié !') }}
                      className="px-3 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 whitespace-nowrap">
                      Copier
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button type="button"
                      onClick={async () => {
                        if (!confirm('Révoquer ce lien ? L\'employé devra utiliser un nouveau lien.')) return
                        setGeneratingToken(true)
                        try {
                          await api.delete(`/employees/${emp.id}/app-token`)
                          setAppToken(null)
                        } finally { setGeneratingToken(false) }
                      }}
                      className="text-xs text-red-500 hover:text-red-700 underline">
                      {generatingToken ? '...' : 'Révoquer'}
                    </button>
                    <button type="button"
                      onClick={async () => {
                        setGeneratingToken(true)
                        try {
                          const r = await api.post(`/employees/${emp.id}/app-token`)
                          setAppToken(r.data.appToken)
                        } finally { setGeneratingToken(false) }
                      }}
                      className="text-xs text-gray-500 hover:text-gray-700 underline">
                      Régénérer
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button"
                  onClick={async () => {
                    setGeneratingToken(true)
                    try {
                      const r = await api.post(`/employees/${emp.id}/app-token`)
                      setAppToken(r.data.appToken)
                    } finally { setGeneratingToken(false) }
                  }}
                  disabled={generatingToken}
                  className="mt-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {generatingToken ? 'Génération...' : '🔗 Générer le lien personnel'}
                </button>
              )}
              <p className="text-xs text-gray-400 mt-1">L'employé bookmarke ce lien et se connecte automatiquement.</p>
            </div>
            <div>
              <label className={lbl}>Notes internes (confidentielles)</label>
              <textarea value={form.notes || ''} rows={4} placeholder="Notes..."
                onChange={e => setForm((ff: any) => ({ ...ff, notes: e.target.value }))}
                className={`${inp} resize-none`} />
            </div>
          </div>
        </div>
      )}

      {/* ── CONTRAT ── */}
      {tab === 'contract' && (
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-4">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Type de contrat</h2>
            <div>
              <label className={lbl}>Type *</label>
              <select value={form.contractType || 'CDI'}
                onChange={e => setForm((ff: any) => ({ ...ff, contractType: e.target.value }))} className={inp}>
                {['CDI','CDD','Saisonnier','Extra','Stage','Intérim'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Volume horaire hebdomadaire *</label>
              <div className="flex items-center gap-2">
                <input type="number" value={form.weeklyHours || 35} min={1} max={60}
                  onChange={e => setForm((ff: any) => ({ ...ff, weeklyHours: +e.target.value }))} className={`${inp} w-24`} />
                <span className="text-sm text-gray-500">h/sem · {((form.weeklyHours || 35) * 4.33).toFixed(1)} h/mois</span>
              </div>
            </div>
            {f('Date d\'entrée', 'contractStartDate', 'date')}
            {(form.contractType === 'CDD' || form.contractType === 'Stage') && f('Date de fin', 'contractEndDate', 'date')}
            <div>
              <label className={lbl}>Établissement principal</label>
              <select value={form.establishmentId || ''} onChange={e => setForm((ff: any) => ({ ...ff, establishmentId: e.target.value }))} className={inp}>
                {establishments.map((e: any) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Poste principal</label>
              <select value={form.positionId || ''} onChange={e => setForm((ff: any) => ({ ...ff, positionId: e.target.value || null }))} className={inp}>
                <option value="">— Aucun poste défini —</option>
                {positions.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-4">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Convention IDCC 1505</h2>
            <div>
              <label className={lbl}>Niveau de classification</label>
              <select value={form.conventionLevel || 'E1'}
                onChange={e => {
                  const lvl = CCN_LEVELS.find(l => l.value === e.target.value)
                  setForm((ff: any) => ({ ...ff, conventionLevel: e.target.value, hourlyRate: lvl?.rate ?? ff.hourlyRate }))
                }} className={inp}>
                {CCN_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label} — {l.rate.toFixed(3)} €/h</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Taux horaire brut (€/h)</label>
              <input type="number" step="0.001" value={form.hourlyRate || 12.063}
                onChange={e => setForm((ff: any) => ({ ...ff, hourlyRate: +e.target.value }))} className={inp} />
              <p className="text-xs text-gray-400 mt-1">Auto-rempli selon le niveau. Modifiable si taux négocié.</p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Salaire mensuel brut estimé</p>
              <p className="text-2xl font-bold text-blue-700">{monthlySalary.toFixed(2)} €</p>
              <p className="text-xs text-gray-400">{((form.weeklyHours || 35) * 4.33).toFixed(1)}h × {(form.hourlyRate || 12.063).toFixed(3)} €/h</p>
              <div className="mt-2 pt-2 border-t border-blue-200">
                <p className="text-xs text-gray-500">Coût employeur estimé (charg. {form.employerCostRate || 45}%)</p>
                <p className="text-lg font-bold text-indigo-700">{employerCost.toFixed(2)} €/mois · {hourlyEmployerCost.toFixed(2)} €/h</p>
              </div>
            </div>
            <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Afficher au planning</p>
                <p className="text-xs text-gray-400">Visible dans la vue planning</p>
              </div>
              <button type="button" onClick={() => setForm((ff: any) => ({ ...ff, showOnPlanning: !ff.showOnPlanning }))}
                className={form.showOnPlanning ? 'text-blue-600' : 'text-gray-400'}>
                {form.showOnPlanning ? <ToggleRight size={30} /> : <ToggleLeft size={30} />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADMINISTRATIF ── */}
      {tab === 'admin' && (
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-4">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Obligations légales</h2>
            {f('Date DPAE', 'dpaeDate', 'date')}
            {f('Date visite médicale', 'medicalVisitDate', 'date')}
            {f('Date expiration visite', 'medicalVisitExpiry', 'date')}
            {/* Statut visite médicale */}
            {form.medicalVisitExpiry && (() => {
              const exp = new Date(form.medicalVisitExpiry)
              const daysLeft = Math.round((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
              return (
                <div className={`rounded-lg px-3 py-2 text-sm ${daysLeft < 0 ? 'bg-red-50 text-red-700' : daysLeft < 60 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
                  {daysLeft < 0 ? `⚠️ Visite expirée il y a ${Math.abs(daysLeft)} jours` : `Visite valide — expire dans ${daysLeft} jours`}
                </div>
              )
            })()}
          </div>
          <div className="space-y-4">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Coordonnées bancaires & mutuelle</h2>
            {f('IBAN / RIB', 'ribIban', 'text', 'FR76 XXXX XXXX XXXX XXXX XXXX XXX')}
            {f('Mutuelle', 'mutuelleName', 'text', 'Nom de la mutuelle')}
            {f('N° adhérent mutuelle', 'mutuelleNumber', 'text')}
            <div className="mt-4">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Documents administratifs en attente</h2>
              <div className="space-y-2">
                {!form.ribIban && <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2"><AlertTriangle size={14} /> RIB manquant</div>}
                {!form.dpaeDate && <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2"><AlertTriangle size={14} /> DPAE non renseignée</div>}
                {!form.socialSecurityNumber && <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2"><AlertTriangle size={14} /> N° Sécu manquant</div>}
                {form.ribIban && form.dpaeDate && form.socialSecurityNumber && (
                  <div className="text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">✓ Dossier administratif complet</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── PLANNING ── */}
      {tab === 'planning' && (
        <div className="space-y-6">
          {/* Planning du jour & collègues */}
          {(() => {
            const myShifts = todayShifts.filter((s: any) => s.employeeId === id)
            const colleagueShifts = todayShifts.filter((s: any) => s.employeeId !== id)
            const todayLabel = new Date(today + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
            return (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Users size={18} className="text-blue-600" />
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Planning du jour — <span className="capitalize">{todayLabel}</span></h2>
                </div>

                {myShifts.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Pas de shift prévu aujourd'hui pour cet employé.</p>
                ) : (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Ses horaires</p>
                    <div className="flex gap-2 flex-wrap">
                      {myShifts.map((s: any) => (
                        <div key={s.id} className="bg-blue-600 text-white rounded-lg px-3 py-2 text-sm font-medium shadow-sm">
                          {s.startTime} – {s.endTime}
                          {s.position && <span className="ml-2 opacity-80 text-xs">({s.position.name})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {colleagueShifts.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-indigo-700 uppercase mb-2">
                      Collègues présents ({colleagueShifts.length} shift{colleagueShifts.length > 1 ? 's' : ''})
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {colleagueShifts.map((s: any) => (
                        <Link key={s.id} to={`/employees/${s.employeeId}`}
                          className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border border-gray-200 hover:border-blue-400 hover:shadow-sm transition-all">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {s.employee?.firstName?.[0]}{s.employee?.lastName?.[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{s.employee?.firstName} {s.employee?.lastName}</p>
                            <p className="text-xs text-gray-500">
                              {s.startTime} – {s.endTime}
                              {s.position && <span className="ml-1 text-indigo-500">• {s.position.name}</span>}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {myShifts.length === 0 && colleagueShifts.length === 0 && (
                  <p className="text-sm text-gray-500 italic">Aucun shift prévu aujourd'hui dans cet établissement.</p>
                )}
              </div>
            )
          })()}

          {/* ── Heures type personnelles ── */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-4">
            <div>
              <h2 className="text-sm font-bold text-amber-800 uppercase tracking-wide">Heures type — utilisées pour les shifts générés</h2>
              <p className="text-xs text-gray-500 mt-1">Si laissé vide, l'algo utilise les heures par défaut de la matrice créneaux.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Matin */}
              <div className="border rounded-lg p-3 bg-white">
                <p className="text-xs font-semibold text-amber-700 mb-2">☀️ Matin (jours normaux)</p>
                <div className="flex items-center gap-2">
                  <input type="time" value={form.matinStart || ''} onChange={e => setForm((ff: any) => ({ ...ff, matinStart: e.target.value || null }))} className="border rounded px-2 py-1 text-sm" />
                  <span className="text-gray-400">→</span>
                  <input type="time" value={form.matinEnd || ''} onChange={e => setForm((ff: any) => ({ ...ff, matinEnd: e.target.value || null }))} className="border rounded px-2 py-1 text-sm" />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Ex Salim : 07:00 → 13:30 (6h30)</p>
              </div>

              {/* Aprem */}
              <div className="border rounded-lg p-3 bg-white">
                <p className="text-xs font-semibold text-orange-700 mb-2">🌙 Après-midi</p>
                <div className="flex items-center gap-2">
                  <input type="time" value={form.apremStart || ''} onChange={e => setForm((ff: any) => ({ ...ff, apremStart: e.target.value || null }))} className="border rounded px-2 py-1 text-sm" />
                  <span className="text-gray-400">→</span>
                  <input type="time" value={form.apremEnd || ''} onChange={e => setForm((ff: any) => ({ ...ff, apremEnd: e.target.value || null }))} className="border rounded px-2 py-1 text-sm" />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Ex Sylvie : 13:15 → 20:15 (7h)</p>
              </div>
            </div>

            <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
              <p className="text-xs font-semibold text-blue-700 mb-2">🚚 Heure début matin sur jour livraison</p>
              <input type="time" value={form.deliveryMatinStart || ''} onChange={e => setForm((ff: any) => ({ ...ff, deliveryMatinStart: e.target.value || null }))} className="border rounded px-2 py-1 text-sm" />
              <p className="text-[10px] text-gray-500 mt-1">
                Sur les jours de livraison, l'employé démarre à cette heure (override de l'heure matin habituelle).
                Ex Salim 05:30, Valérie B 06:00. Laissez vide si l'employé démarre à son heure normale même les jours livraison.
              </p>
            </div>
          </div>

          {/* ── Contraintes planning ── */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 space-y-5">
            <h2 className="text-sm font-bold text-indigo-800 uppercase tracking-wide">Contraintes planning — utilisées par le Smart Planner IA</h2>

            {/* Repos par semaine */}
            <div>
              <label className={lbl}>Jours de repos par semaine <span className="text-red-500 font-bold">— imposé au calcul IA</span></label>
              <div className="flex items-center gap-2 mt-1">
                {[1, 2, 3, 4].map(n => (
                  <button key={n} type="button" onClick={() => setForm((ff: any) => ({ ...ff, reposParSemaine: n }))}
                    className={`w-10 h-10 rounded-lg text-sm font-bold border transition-colors ${form.reposParSemaine === n ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'}`}>
                    {n}
                  </button>
                ))}
                <span className="text-xs text-gray-500">jour{form.reposParSemaine > 1 ? 's' : ''}/sem</span>
              </div>
            </div>

            {/* Jours de repos préférés */}
            <div>
              <label className={lbl}>Jours de repos préférés <span className="text-gray-400 font-normal">(non-bloquant, indicatif pour l'IA)</span></label>
              <div className="flex gap-2 flex-wrap mt-1">
                {DAYS_FR.map((d, i) => {
                  const sel = (form.joursReposPreference || []).includes(i)
                  return (
                    <button key={d.key} type="button" onClick={() => setForm((ff: any) => ({
                      ...ff,
                      joursReposPreference: sel
                        ? ff.joursReposPreference.filter((x: number) => x !== i)
                        : [...(ff.joursReposPreference || []), i]
                    }))}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${sel ? 'bg-indigo-100 text-indigo-700 border-indigo-400' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-300'}`}>
                      {d.label}
                    </button>
                  )
                })}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">L'IA essaiera de respecter ces préférences si le planning le permet, sans les imposer.</p>
            </div>

            {/* Cycle 5 semaines */}
            <div>
              <label className={lbl}>Fréquence maximale sur cycle 5 semaines</label>
              <div className="grid grid-cols-3 gap-4 mt-2">
                <div>
                  <p className="text-xs text-gray-500 mb-1.5">☀️ Dimanches matin</p>
                  <div className="flex gap-1">
                    {[0,1,2,3,4,5].map(n => (
                      <button key={n} type="button" onClick={() => setForm((ff: any) => ({ ...ff, dimancheMatinParCycle: n }))}
                        className={`w-8 h-8 rounded text-xs font-bold border ${form.dimancheMatinParCycle === n ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1.5">🌆 Dimanches après-midi</p>
                  <div className="flex gap-1">
                    {[0,1,2,3,4,5].map(n => (
                      <button key={n} type="button" onClick={() => setForm((ff: any) => ({ ...ff, dimancheApremParCycle: n }))}
                        className={`w-8 h-8 rounded text-xs font-bold border ${form.dimancheApremParCycle === n ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1.5">📅 Samedis</p>
                  <div className="flex gap-1">
                    {[0,1,2,3,4,5].map(n => (
                      <button key={n} type="button" onClick={() => setForm((ff: any) => ({ ...ff, samediParCycle: n }))}
                        className={`w-8 h-8 rounded text-xs font-bold border ${form.samediParCycle === n ? 'bg-purple-500 text-white border-purple-500' : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">Ex : 2 dimanches matin sur 5 semaines = cet employé ne travaille pas plus de 2 dimanches matin par cycle de 5 semaines.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8">
            <div>
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4">Disponibilités hebdomadaires</h2>
              <p className="text-xs text-gray-500 mb-3">Jours où l'employé est disponible par défaut.</p>
              <div className="flex gap-2 flex-wrap mb-4">
                {DAYS_FR.map(d => {
                  const active = !!form.availabilities?.[d.key]
                  return (
                    <button key={d.key} type="button" onClick={() => toggleAvailability(d.key)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
                      {d.label}
                    </button>
                  )
                })}
              </div>
              {Object.entries(form.availabilities || {}).map(([day, slots]: any) => {
                const dayLabel = DAYS_FR.find(d => d.key === day)?.label || day
                return (
                  <div key={day} className="flex items-center gap-3 mb-2">
                    <span className="text-sm text-gray-600 w-10">{dayLabel}</span>
                    <input type="time" value={slots[0]?.start || '09:00'}
                      onChange={e => setForm((ff: any) => ({ ...ff, availabilities: { ...ff.availabilities, [day]: [{ ...slots[0], start: e.target.value }] } }))}
                      className="border border-gray-300 rounded px-2 py-1 text-sm" />
                    <span className="text-gray-400">–</span>
                    <input type="time" value={slots[0]?.end || '17:00'}
                      onChange={e => setForm((ff: any) => ({ ...ff, availabilities: { ...ff.availabilities, [day]: [{ ...slots[0], end: e.target.value }] } }))}
                      className="border border-gray-300 rounded px-2 py-1 text-sm" />
                  </div>
                )
              })}
            </div>
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Indisponibilités récurrentes</h2>
                <button type="button" onClick={addUnavailability}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                  <Plus size={13} /> Ajouter
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">Créneaux à éviter lors de la planification.</p>
              <div className="space-y-2">
                {(form.unavailabilities || []).map((u: any, i: number) => {
                  const upd = (patch: any) => {
                    const updated = [...form.unavailabilities]
                    updated[i] = { ...u, ...patch }
                    setForm((ff: any) => ({ ...ff, unavailabilities: updated }))
                  }
                  return (
                  <div key={i} className="bg-red-50 border border-red-100 rounded-lg p-2 space-y-1">
                    <div className="flex items-center gap-2">
                      <select value={u.day} onChange={e => upd({ day: e.target.value })}
                        className="border border-red-200 rounded px-2 py-1 text-xs bg-white">
                        {DAYS_FR.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                      </select>
                      <input type="time" value={u.start} onChange={e => upd({ start: e.target.value })}
                        className="border border-red-200 rounded px-2 py-1 text-xs bg-white" />
                      <span className="text-red-400 text-xs">–</span>
                      <input type="time" value={u.end} onChange={e => upd({ end: e.target.value })}
                        className="border border-red-200 rounded px-2 py-1 text-xs bg-white" />
                      <input value={u.label || ''} placeholder="Raison"
                        onChange={e => upd({ label: e.target.value })}
                        className="flex-1 border border-red-200 rounded px-2 py-1 text-xs bg-white" />
                      <button type="button" onClick={() => removeUnavailability(i)} className="text-red-400 hover:text-red-600">
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 pl-1">
                      <span className="text-xs text-red-400">Période&nbsp;:</span>
                      <input type="date" value={u.dateFrom || ''} onChange={e => upd({ dateFrom: e.target.value || undefined })}
                        className="border border-red-200 rounded px-2 py-1 text-xs bg-white" />
                      <span className="text-red-400 text-xs">au</span>
                      <input type="date" value={u.dateTo || ''} onChange={e => upd({ dateTo: e.target.value || undefined })}
                        className="border border-red-200 rounded px-2 py-1 text-xs bg-white" />
                      <span className="text-xs text-gray-400 italic">(optionnel — vide = toujours)</span>
                    </div>
                  </div>
                  )
                })}
                {(form.unavailabilities || []).length === 0 && (
                  <p className="text-xs text-gray-400 italic">Aucune indisponibilité enregistrée</p>
                )}
              </div>
            </div>
          </div>

          {/* Compétences par priorité */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Compétences / Spécialités</h2>
                <p className="text-xs text-gray-500 mt-0.5">Classées par ordre de priorité — la 1ère est la principale.</p>
              </div>
              <select
                onChange={e => {
                  const v = e.target.value  // capture la valeur AVANT que e.target.value soit reset
                  if (v) {
                    setForm((ff: any) => ({ ...ff, skills: [...(ff.skills || []).filter((s: string) => s !== v), v] }))
                    e.target.value = ''
                  }
                }}
                className="text-xs border border-blue-200 rounded-lg px-2 py-1 text-blue-700 bg-blue-50 outline-none focus:border-blue-400 max-w-[180px]"
                defaultValue="">
                <option value="">+ Ajouter un poste</option>
                {positions.filter((p: any) => !(form.skills || []).includes(p.name)).map((p: any) => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2 max-w-md">
              {(form.skills || []).length === 0 && (
                <p className="text-xs text-gray-400 italic">Aucune compétence renseignée</p>
              )}
              {(form.skills || []).map((skill: string, i: number) => (
                <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                  <GripVertical size={14} className="text-gray-300 flex-shrink-0" />
                  <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    {i === 0 ? <Star size={10} /> : i + 1}
                  </span>
                  <span className="flex-1 text-sm text-gray-800">{skill || <span className="text-gray-400 italic">vide</span>}</span>
                  <div className="flex gap-1 flex-shrink-0">
                    {i > 0 && (
                      <button type="button" onClick={() => {
                        const updated = [...(form.skills || [])]
                        ;[updated[i - 1], updated[i]] = [updated[i], updated[i - 1]]
                        setForm((ff: any) => ({ ...ff, skills: updated }))
                      }} className="text-gray-300 hover:text-blue-500 text-xs px-1" title="Monter">↑</button>
                    )}
                    {i < (form.skills || []).length - 1 && (
                      <button type="button" onClick={() => {
                        const updated = [...(form.skills || [])]
                        ;[updated[i], updated[i + 1]] = [updated[i + 1], updated[i]]
                        setForm((ff: any) => ({ ...ff, skills: updated }))
                      }} className="text-gray-300 hover:text-blue-500 text-xs px-1" title="Descendre">↓</button>
                    )}
                    <button type="button" onClick={() => {
                      const updated = (form.skills || []).filter((_: any, idx: number) => idx !== i)
                      setForm((ff: any) => ({ ...ff, skills: updated }))
                    }} className="text-gray-300 hover:text-red-500">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── PAIE ── */}
      {tab === 'payroll' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-4">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Paramètres paie</h2>
              <div>
                <label className={lbl}>Taux charges patronales (%)</label>
                <div className="flex items-center gap-2">
                  <input type="number" min={0} max={100} step={0.1} value={form.employerCostRate || 45}
                    onChange={e => setForm((ff: any) => ({ ...ff, employerCostRate: +e.target.value }))} className={`${inp} w-24`} />
                  <span className="text-sm text-gray-500">% du brut</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">Inclut URSSAF, prévoyance, mutuelle patronale…</p>
              </div>
              <div>
                <label className={lbl}>Indemnité transport mensuelle (€)</label>
                <input type="number" min={0} step={0.01} value={form.transportAllowance || 0}
                  onChange={e => setForm((ff: any) => ({ ...ff, transportAllowance: +e.target.value }))} className={inp} />
              </div>
              <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Tickets restaurant</p>
                  <p className="text-xs text-gray-400">Bénéficiaire du titre restaurant</p>
                </div>
                <button type="button" onClick={() => setForm((ff: any) => ({ ...ff, mealVoucherEnabled: !ff.mealVoucherEnabled }))}
                  className={form.mealVoucherEnabled ? 'text-blue-600' : 'text-gray-400'}>
                  {form.mealVoucherEnabled ? <ToggleRight size={30} /> : <ToggleLeft size={30} />}
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Récapitulatif coût</h2>
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Salaire brut mensuel</span>
                  <span className="font-semibold">{monthlySalary.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Charges patronales ({form.employerCostRate || 45}%)</span>
                  <span className="font-semibold">{(monthlySalary * (form.employerCostRate || 45) / 100).toFixed(2)} €</span>
                </div>
                {form.transportAllowance > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Indemnité transport</span>
                    <span className="font-semibold">{form.transportAllowance.toFixed(2)} €</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-bold pt-2 border-t border-indigo-200">
                  <span>Coût employeur total</span>
                  <span className="text-indigo-700">{(employerCost + (form.transportAllowance || 0)).toFixed(2)} €/mois</span>
                </div>
                <p className="text-xs text-gray-400">Soit {hourlyEmployerCost.toFixed(2)} €/h tout compris</p>
              </div>
            </div>
          </div>

          {/* Primes & Variables */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Primes & variables</h2>
            </div>
            <div className="space-y-2 mb-3">
              {(form.primes || []).map((p: any, i: number) => (
                <div key={i} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-2">
                  <span className="flex-1 text-sm font-medium text-gray-700">{p.label}</span>
                  <span className="text-sm font-bold text-gray-900">{p.amount.toFixed(2)} €</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${p.recurring ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                    {p.recurring ? 'Mensuelle' : 'Ponctuelle'}
                  </span>
                  <button type="button" onClick={() => removePrime(i)} className="text-gray-400 hover:text-red-500">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-3">
              <input value={newPrime.label} onChange={e => setNewPrime(p => ({ ...p, label: e.target.value }))}
                placeholder="Libellé (ex: Prime ancienneté)" className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input type="number" value={newPrime.amount} onChange={e => setNewPrime(p => ({ ...p, amount: e.target.value }))}
                placeholder="Montant €" className="w-28 border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <select value={newPrime.recurring ? 'true' : 'false'} onChange={e => setNewPrime(p => ({ ...p, recurring: e.target.value === 'true' }))}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm">
                <option value="true">Mensuelle</option>
                <option value="false">Ponctuelle</option>
              </select>
              <button type="button" onClick={addPrime}
                className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700">
                <Plus size={13} /> Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── COMPTEURS ── */}
      {tab === 'counters' && (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'CP acquis', value: cpAcquired.toFixed(1), sub: form.cpManual != null ? 'modifié manuellement' : `${monthsWorked} mois × 2.5j`, color: 'text-gray-900' },
              { label: 'CP pris', value: cpTaken.toFixed(1), sub: 'demandes approuvées', color: 'text-orange-500' },
              { label: 'Solde CP', value: cpBalance.toFixed(1), sub: 'jours restants', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
            ].map(c => (
              <div key={c.label} className={`bg-white rounded-xl border p-5 ${c.bg || 'border-gray-200'}`}>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{c.label}</p>
                <p className={`text-3xl font-bold ${c.color}`}>{c.value}</p>
                <p className="text-xs text-gray-400 mt-1">{c.sub}</p>
              </div>
            ))}
          </div>
          {/* Correction manuelle CP */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4">
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">Ajuster les CP acquis</p>
              <p className="text-xs text-amber-600 mt-0.5">Calcul auto : {cpAutoAcquired.toFixed(1)}j ({monthsWorked} mois × 2.5j). Tu peux corriger si nécessaire.</p>
            </div>
            <div className="flex items-center gap-2">
              <input type="number" min={0} max={50} step={0.5}
                value={form.cpManual ?? cpAutoAcquired}
                onChange={e => setForm((ff: any) => ({ ...ff, cpManual: +e.target.value }))}
                className="w-20 border border-amber-300 rounded-lg px-2 py-1.5 text-sm text-center font-bold focus:outline-none focus:ring-2 focus:ring-amber-400" />
              <span className="text-sm text-amber-700">jours</span>
              {form.cpManual != null && (
                <button type="button" onClick={() => setForm((ff: any) => ({ ...ff, cpManual: null }))}
                  className="text-xs text-amber-500 hover:text-amber-700 underline">Réinitialiser</button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Heures planifiées (total)</p>
              <p className="text-3xl font-bold text-gray-900">{totalShiftHours.toFixed(1)}h</p>
              <p className="text-xs text-gray-400 mt-1">{shifts.length} shifts enregistrés</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Absences approuvées</p>
              <p className="text-3xl font-bold text-gray-900">{absences.filter((a: any) => a.status === 'approved').length}</p>
              <p className="text-xs text-gray-400 mt-1">demandes validées</p>
            </div>
          </div>
          {absences.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Absences par type</h3>
              <div className="space-y-2">
                {Object.entries(
                  absences.filter((a: any) => a.status === 'approved').reduce((acc: any, a: any) => {
                    const key = a.absenceType?.name || 'Autre'
                    acc[key] = (acc[key] || 0) + (a.durationDays || 0)
                    return acc
                  }, {})
                ).map(([type, days]: any) => (
                  <div key={type} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{type}</span>
                    <span className="font-semibold">{days}j</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── HISTORIQUE ── */}
      {tab === 'history' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Derniers shifts ({shifts.length})</h2>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {shifts.length === 0 ? <p className="text-sm text-gray-400 p-4 text-center">Aucun shift</p> : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Date</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Horaires</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Poste</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.slice().sort((a: any, b: any) => b.date.localeCompare(a.date)).slice(0, 30).map((s: any) => {
                      const d = new Date(s.date + 'T12:00:00Z')
                      return (
                        <tr key={s.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-700">{d.getUTCDate()} {MONTHS[d.getUTCMonth()]} {d.getUTCFullYear()}</td>
                          <td className="px-4 py-2 font-mono text-gray-800">{s.startTime}–{s.endTime}</td>
                          <td className="px-4 py-2">
                            {s.position ? <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: s.position.color }}>{s.position.name}</span> : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {s.status === 'published' ? 'Publié' : 'Brouillon'}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Absences ({absences.length})</h2>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {absences.length === 0 ? <p className="text-sm text-gray-400 p-4 text-center">Aucune absence</p> : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Type</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Période</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500">Durée</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {absences.slice().sort((a: any, b: any) => b.startDate.localeCompare(a.startDate)).map((a: any) => {
                      const S: Record<string, string> = { pending: 'bg-yellow-100 text-yellow-700', approved: 'bg-green-100 text-green-700', refused: 'bg-red-100 text-red-700' }
                      const L: Record<string, string> = { pending: 'En attente', approved: 'Approuvé', refused: 'Refusé' }
                      return (
                        <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2"><span className="text-xs font-medium px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: a.absenceType?.color }}>{a.absenceType?.name}</span></td>
                          <td className="px-4 py-2 text-gray-600">{a.startDate} → {a.endDate}</td>
                          <td className="px-4 py-2 text-center text-gray-700">{a.durationDays}j</td>
                          <td className="px-4 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${S[a.status]}`}>{L[a.status]}</span></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── DOCUMENTS ── */}
      {tab === 'documents' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Documents ({docs.length})</h2>
            <Link to="/documents" className="text-sm text-blue-600 hover:text-blue-800 font-medium">Gérer les documents →</Link>
          </div>
          {docs.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <FolderOpen size={32} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400">Aucun document pour cet employé</p>
              <Link to="/documents" className="text-sm text-blue-600 mt-2 inline-block">Ajouter un document</Link>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {docs.map((doc: any, i: number) => (
                <div key={doc.id} className={`flex items-center gap-3 p-4 ${i > 0 ? 'border-t border-gray-100' : ''}`}>
                  <div className="text-2xl">📄</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 truncate">{doc.name}</div>
                    <div className="text-xs text-gray-400">{doc.documentType} · {new Date(doc.createdAt).toLocaleDateString('fr-FR')}</div>
                  </div>
                  <a href={`/api/documents/${doc.id}/download`} target="_blank" className="text-blue-600 hover:text-blue-800 text-sm font-medium">Télécharger</a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
